import { Module, MutationTree } from "vuex";
import { RootState } from "@/store/index";
import { MapFilter } from "@/types/MapFilter";
import { Attendee } from "@/types/Attendee";
import { Place } from "@/types/Place";
import { Corpus } from "@/types/Corpus";
import axios from 'axios';
import { corporaList } from '@/data/corporaInfo';

interface MapFilterState {
    instance: MapFilter;
}

const mutations: MutationTree<MapFilterState> = {
    toggleFilter(state: MapFilterState) {
       state.instance.isOpen = !state.instance.isOpen;
    },
    addSpeaker(state: MapFilterState, speaker: Attendee) {
       state.instance.speakers.push(speaker);
    },
    addPlace(state: MapFilterState, place: Place) {
       state.instance.places.push(place);
    },
    removeSpeaker(state: MapFilterState, speaker: Attendee) {
        const i = state.instance.speakers.indexOf(speaker);
        state.instance.speakers.splice(i, 1);
    },
    removePlace(state: MapFilterState, place: Place) {
        const i = state.instance.places.indexOf(place);
        state.instance.places.splice(i, 1);
    },
    toggleFilterMenu(state: MapFilterState) {
        state.instance.menuIsOpen = !state.instance.menuIsOpen;
    },
    updateCorpusList(state: MapFilterState, corpusList: Corpus[]) {
        state.instance.corpora = corporaList;
    },
    toggleCorpora (state: MapFilterState, corpora: Corpus) {
        const i = state.instance.corpora.indexOf(corpora);
        if(i >= 0) {
            state.instance.corpora.splice(i,1);
        } else {
            state.instance.corpora.push(corpora);
        }
    },
    updateFromDate (state: MapFilterState, fromDate :number) {
        state.instance.fromDate = fromDate;
    },
    updateToDate (state: MapFilterState, toDate :number) {
        state.instance.toDate = toDate;
    },
    updateAllPlaces (state: MapFilterState, allPlaces :Place[]) {
        state.instance.allPlaces = allPlaces;
    },
    changeLoading(state: MapFilterState, isLoading :boolean) {
        state.instance.isLoading = isLoading;
    },
    updatePlaceMentions(state: MapFilterState, meetings :any[]) {
        const analyzedPlacesMap = new Map();
        meetings.forEach((obj) => {
            analyzedPlacesMap.set(obj.place_name, {
                coordinates: obj.coordinates,
                total: obj.total,
                speakers: obj.speakers
            })
        })
        state.instance.placeMentions = analyzedPlacesMap;
    },
    updateAllSpeakers(state: MapFilterState, speakers :Attendee[]){
        state.instance.allSpeakers = speakers;
    },
    togglePlaceVisibility(state: MapFilterState, place :[string, any]) {
        if (state.instance.visiblePlaces.has(place[0])) {
            state.instance.visiblePlaces.delete(place[0]);
        } else {
            state.instance.visiblePlaces.set(place[0], place[1]);
        }
    },
    setPlaceVisibility(state: MapFilterState, payload:  {place :[string, any], isVisible: boolean}){
        const {place, isVisible} = payload;
        
        if(isVisible && !state.instance.visiblePlaces.has(place[0])) {
            state.instance.visiblePlaces.set(place[0], place[1]);
        } 
        else if (!isVisible && state.instance.visiblePlaces.has(place[0]) ) {
            state.instance.visiblePlaces.delete(place[0]);
        }
    },
    emptyPlaceFilter (state: MapFilterState){
        state.instance.visiblePlaces.clear();
    },
    setInspectedPlace(state: MapFilterState, inspectedPlace : [string, any] | null) {
        state.instance.inspectedPlace = inspectedPlace;
    },
    setInspectedPlaceSpeakers(state: MapFilterState, speakers: any[] | null) {
        state.instance.inspectedPlaceSpeakers = speakers;
    },
}

const mapFilterModule: Module<MapFilterState, RootState> = {
    namespaced: true,
    state: {
        instance: MapFilter.create()
    },
    getters: {
        MapFilterInstance: (state) => state.instance,
        FilterIsSet(state) { 
            return state.instance?.speakers.length > 0  || state.instance?.places.length > 0 || state.instance?.corpora.length < 2 || state.instance?.fromDate > 1861 || state.instance?.toDate < 1939;
        },
        hasCorpora (state) {
            return (corp : Corpus) => {
                return state.instance?.corpora.includes(corp)
            }
        },
        hasEmptyCorpora(state) {
            return state.instance?.corpora.length == 0;
        },
    },
    mutations,
    actions: {
        async init(context, {locale} : {locale: string}) {
            //initalize the list of corporas
            await context.dispatch('getCorpora');
            //get the list of the places based on the corpora they are mentioned in and the place that is set in the filter
            await context.dispatch('getPlacesList', locale);            
        },
        async getPlacesList(context, locale) {
            context.commit('changeLoading', true)

            //clear the filter on map:
            context.commit('emptyPlaceFilter')
            
            const startTime = performance.now()
            const payload = { 
                speakers: context.getters.MapFilterInstance.speakers,
                places: context.getters.MapFilterInstance.places ? context.getters.MapFilterInstance.places.map((place :any) => place.names.sl !== "zzzzz" ? place.names.sl : place.names.de) : [],
                fromDate: context.getters.MapFilterInstance.fromDate,
                toDate: context.getters.MapFilterInstance.toDate,
                corpus: context.getters.MapFilterInstance.corpora,
            }
            
            axios.post( process.env.VUE_APP_API_URL +  '/krajiOmembe/getPlaces', payload)
                .then(response => {
                        const meetingsList = response.data
                        context.commit('updatePlaceMentions', meetingsList)
                    }
                )
                .catch(error => {
                    console.log(error);
                })
                .finally (() => {
                    context.commit('changeLoading', false);
                });
        },  
        getCorpora(context) {
            let minDateFrom = context.state.instance.fromDate;
            let maxDateTo = context.state.instance.toDate;
            let languages = new Set<string>()
            let allLanguages :Set<string> = new Set<string>();
            let corpusList :Corpus[] = [];

            corporaList.forEach((corpus: Corpus) => {
                if (minDateFrom == null || corpus.dateFrom.getFullYear() < minDateFrom) {
                    minDateFrom = corpus.dateFrom.getFullYear()
                }
                if (maxDateTo == null || corpus.dateTo.getFullYear() > maxDateTo) {
                    maxDateTo = corpus.dateTo.getFullYear()
                }
                corpus.languages.forEach((language: string) => {
                    languages.add(language)
                    allLanguages.add(language)
                })
                corpusList.push(corpus)
            })
            context.commit('updateCorpusList', corpusList) 
        },
        toggleFilter (context) {
            context.commit('toggleFilter')
        },
        async addPlace (context, place ) {
            if(place) {
                context.commit('addPlace', place);
            }
        },
        async removePlace (context, place) {
            context.commit('removePlace', place);
        },
        async addSpeaker (context, speaker ) {
            if(speaker?.names.length > 0 ) {
                context.commit('addSpeaker', speaker); 
            }
        },
        async removeSpeaker (context, speaker) {
            context.commit('removeSpeaker', speaker);
        },
        toggleFilterMenu (context) {
            context.commit('toggleFilterMenu');
        },
        addCorpora (context, corpora) {
            context.commit('addCorpora',  corpora)
        },
        toggleCorpora (context, corpora) {
            context.commit('toggleCorpora', corpora)
        },
        updateFromDate (context, date) {
            context.commit('updateFromDate', date)
        },
        updateToDate (context, date) {
            context.commit('updateToDate', date)
        },
        updateAllPlaces (context, allPlaces) {
            context.commit('updateAllPlaces', allPlaces)
        },
        updateAllSpeakers (context, allSpeakers) {
            context.commit('updateAllSpeakers', allSpeakers)
        },
        togglePlaceVisibility (context, place) {
            context.commit('togglePlaceVisibility', place)
        },
        setPlaceVisibility (context, {place, isVisible}) {
            context.commit('setPlaceVisibility', {place, isVisible})
        },
        emptyPlaceFilter (context) {
            // todo: loader ko briše places
            context.commit('emptyPlaceFilter');
        },
        changeLoading (context, isLoading) {
            context.commit('changeLoading', isLoading)
        },
        setInspectedPlace(context, inspectedPlace ) {
            context.commit('setInspectedPlace', inspectedPlace);
        },
        setInspectedPlaceSpeakers(context, speakers){
            context.commit('setInspectedPlaceSpeakers', speakers)
        }
    }
};

export default mapFilterModule;